import type { RepFieldAudit } from "@repo/models";

import { styles } from "@repo/helpers";
import { Button, MoveableDialog } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import type { FieldAuditsFilter } from "./types";

import FieldAuditsApi, { controller } from "../../api/fieldAuditsApi";
import { QUERY_KEYS } from "../../api/queries";
import { AgentPortal } from "../../apps/agentPortal";
import { AuditEditor } from "../../apps/auditEditor";
import { TLPGrid } from "../../components/tlpGrid";
import { getInitialDialogSize } from "../../helpers/agentPortal";
import { useGridColumns } from "../../hooks/useGridColumns";

interface Props {
	filters: FieldAuditsFilter;
	setFilters: (filters: FieldAuditsFilter) => void;
}

export function FieldAuditsGrid({ filters, setFilters }: Props) {
	const [dialogOpen, setDialogOpen] = useState(false);
	const [selectedAudit, setSelectedAudit] = useState<RepFieldAudit | undefined>(undefined);
	const [auditDialogOpen, setAuditDialogOpen] = useState(false);
	const handleAuditDialogOpenChange = (open: boolean) => setAuditDialogOpen(open);

	const fieldAuditsQuery = useQuery({
		queryKey: [QUERY_KEYS.FieldAudits.Grid, filters],
		queryFn: ({ signal }) => FieldAuditsApi.getFieldAudits(filters, { signal }),
	});

	const openPortalModal = useCallback((audit: RepFieldAudit) => {
		setSelectedAudit(audit);
		setDialogOpen(true);
	}, []);

	const { columns, isLoading: isLoadingColumns } = useGridColumns("FieldAudits", FieldAuditsApi.getGridColumns, {
		openPortalModal,
	});

	if (isLoadingColumns) return null;

	return (
		<>
			<TLPGrid<RepFieldAudit, FieldAuditsFilter>
				id="fieldAuditsGrid"
				title="Field Audits"
				gridNameForExport="FieldAudits"
				keyProperty="representativeFieldAuditId"
				filters={filters}
				setFilters={setFilters}
				query={fieldAuditsQuery}
				apiRoute={controller}
				gridApi={FieldAuditsApi}
				columns={columns}
				exportFunc={FieldAuditsApi.getFieldAudits}
				selected={selectedAudit?.representativeFieldAuditId ? [selectedAudit.representativeFieldAuditId] : []}
			/>

			<div className="mt-4">
				<Button onClick={() => setAuditDialogOpen(true)}>View Automated Exports</Button>
			</div>

			{selectedAudit && (
				<MoveableDialog
					open={dialogOpen}
					onOpenChange={() => setDialogOpen(false)}
					title="Rep Portal"
					initialSize={getInitialDialogSize()}
					resizable
					movable
					className={styles("agent-portal-modal")}
				>
					<AgentPortal
						key={selectedAudit?.representativeFieldAuditId ?? 0}
						repId={selectedAudit?.representativeId ?? null}
						selectedTab="FieldAudits"
						entityId={selectedAudit?.representativeFieldAuditId}
					/>
				</MoveableDialog>
			)}

			{auditDialogOpen ? (
				<MoveableDialog
					open={auditDialogOpen}
					onOpenChange={handleAuditDialogOpenChange}
					title="Automated Exports"
					initialSize={getInitialDialogSize()}
					resizable
					movable
				>
					<AuditEditor code="FIELDAUDITS" defaultValues={[]} />
				</MoveableDialog>
			) : null}
		</>
	);
}
